import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  PaginatedResponse,
  LoginRequest,
  LoginResponse,
  User,
  UserProfile,
  Session,
} from '@/shared';

class AdminApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    // Use environment variable for API URL, fallback to /api for dev proxy
    const baseURL = import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/admin`
      : '/api/admin';

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to ensure token is set on every request
    this.client.interceptors.request.use(config => {
      // Get token from instance or localStorage as fallback
      const token = this.token || localStorage.getItem('admin_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      response => response,
      async (error: AxiosError<ApiResponse>) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          localStorage.removeItem('admin_role');
          localStorage.removeItem('admin_is_super_admin');
          localStorage.removeItem('admin_permissions');
          this.token = null;
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string | null) {
    this.token = token;
  }

  // Auth
  auth = {
    login: async (data: LoginRequest): Promise<LoginResponse> => {
      // Use admin-specific login endpoint
      const response = await this.client.post<ApiResponse<LoginResponse>>('/auth/login', data);
      return response.data.data!;
    },

    verify2FA: async (twoFactorToken: string, code: string): Promise<LoginResponse> => {
      const response = await this.client.post<ApiResponse<LoginResponse>>('/auth/verify-2fa', {
        two_factor_token: twoFactorToken,
        code,
      });
      return response.data.data!;
    },

    logout: async (): Promise<void> => {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
    },
  };

  // Dashboard
  dashboard = {
    getStats: async (): Promise<{
      total_users: number;
      active_assets: number;
      orders_today: number;
      volume_today: number;
      pending_kyc: number;
      pending_assets: number;
      pending_withdrawals: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/dashboard');
      return response.data.data!;
    },
  };

  // Users
  users = {
    list: async (params?: {
      page?: number;
      per_page?: number;
      status?: string;
      kyc_level?: number;
      search?: string;
    }): Promise<PaginatedResponse<User>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<User>>>('/users', { params });
      return response.data.data!;
    },

    get: async (id: string): Promise<{
      user: UserProfile;
      kyc: any;
      wallets: any[];
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/users/${id}`);
      return response.data.data!;
    },

    updateStatus: async (id: string, status: string, reason?: string): Promise<void> => {
      await this.client.patch(`/users/${id}/status`, { status, reason });
    },

    suspend: async (id: string, reason: string): Promise<void> => {
      await this.client.post(`/users/${id}/suspend`, { reason });
    },

    unsuspend: async (id: string): Promise<void> => {
      await this.client.post(`/users/${id}/unsuspend`);
    },

    ban: async (id: string, reason: string): Promise<void> => {
      await this.client.post(`/users/${id}/ban`, { reason });
    },

    unban: async (id: string): Promise<void> => {
      await this.client.post(`/users/${id}/unban`);
    },

    resetPassword: async (id: string): Promise<{ temporary_password: string }> => {
      const response = await this.client.post<ApiResponse<{ temporary_password: string }>>(`/users/${id}/reset-password`);
      return response.data.data!;
    },

    disable2FA: async (id: string, reason: string): Promise<void> => {
      await this.client.post(`/users/${id}/disable-2fa`, { reason });
    },

    getSessions: async (id: string): Promise<Session[]> => {
      const response = await this.client.get<ApiResponse<Session[]>>(`/users/${id}/sessions`);
      return response.data.data!;
    },

    revokeSession: async (userId: string, sessionId: string): Promise<void> => {
      await this.client.delete(`/users/${userId}/sessions/${sessionId}`);
    },

    revokeAllSessions: async (id: string): Promise<void> => {
      await this.client.delete(`/users/${id}/sessions`);
    },

    getSecurityEvents: async (id: string, params?: { limit?: number; offset?: number }): Promise<{
      events: any[];
      total: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/users/${id}/security-events`, { params });
      return response.data.data!;
    },

    getLoginAttempts: async (id: string, limit?: number): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>(`/users/${id}/login-attempts`, {
        params: { limit },
      });
      return response.data.data!;
    },

    getApiKeys: async (id: string): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>(`/users/${id}/api-keys`);
      return response.data.data!;
    },

    revokeApiKey: async (userId: string, keyId: string): Promise<void> => {
      await this.client.delete(`/users/${userId}/api-keys/${keyId}`);
    },

    getBalances: async (id: string): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>(`/users/${id}/balances`);
      return response.data.data!;
    },

    adjustBalance: async (id: string, data: {
      asset_id: string;
      wallet_type: string;
      amount: string;
      reason: string;
    }): Promise<void> => {
      await this.client.post(`/users/${id}/adjust-balance`, data);
    },
  };

  // KYC
  kyc = {
    list: async (params?: {
      page?: number;
      per_page?: number;
      status?: string;
      kyc_type?: string;
    }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/kyc/applications', { params });
      return response.data.data!;
    },

    get: async (id: string): Promise<{
      application: any;
      documents: any[];
      review_history: any[];
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/kyc/applications/${id}`);
      return response.data.data!;
    },

    review: async (id: string, action: 'approve' | 'reject', notes?: string, rejectionReason?: string): Promise<void> => {
      await this.client.post(`/kyc/applications/${id}/review`, {
        action,
        notes,
        rejection_reason: rejectionReason,
      });
    },

    requestInfo: async (id: string, message: string, requiredDocuments?: string[]): Promise<void> => {
      await this.client.post(`/kyc/applications/${id}/request-info`, {
        message,
        required_documents: requiredDocuments,
      });
    },

    getDocumentUrl: async (documentId: string): Promise<{ url: string; content_type: string }> => {
      const response = await this.client.get<ApiResponse<any>>(`/kyc/documents/${documentId}/url`);
      return response.data.data!;
    },

    // Market Maker
    listMarketMaker: async (params?: {
      page?: number;
      per_page?: number;
      status?: string;
    }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/kyc/market-maker', { params });
      return response.data.data!;
    },

    reviewMarketMaker: async (id: string, action: 'approve' | 'reject', notes?: string): Promise<void> => {
      await this.client.post(`/kyc/market-maker/${id}/review`, {
        action,
        notes,
      });
    },
  };

  // Assets
  assets = {
    list: async (params?: {
      page?: number;
      per_page?: number;
      phase?: string;
      asset_type?: string;
    }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/assets', { params });
      return response.data.data!;
    },

    get: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/assets/${id}`);
      return response.data.data!;
    },

    updatePhase: async (id: string, phase: string, reason?: string): Promise<void> => {
      await this.client.patch(`/assets/${id}/phase`, { phase, reason });
    },

    updateConfig: async (id: string, config: Record<string, any>): Promise<void> => {
      await this.client.patch(`/assets/${id}/config`, config);
    },
  };

  // Asset Applications
  assetApplications = {
    list: async (params?: {
      page?: number;
      per_page?: number;
      status?: string;
    }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/asset-applications', { params });
      return response.data.data!;
    },

    get: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/asset-applications/${id}`);
      return response.data.data!;
    },

    startReview: async (id: string): Promise<void> => {
      await this.client.post(`/asset-applications/${id}/start-review`);
    },

    requestInfo: async (id: string, message: string, requiredDocuments?: string[]): Promise<void> => {
      await this.client.post(`/asset-applications/${id}/request-info`, {
        message,
        required_documents: requiredDocuments,
      });
    },

    approve: async (id: string, notes?: string): Promise<{ asset_id: string }> => {
      const response = await this.client.post<ApiResponse<{ asset_id: string }>>(`/asset-applications/${id}/approve`, { notes });
      return response.data.data!;
    },

    reject: async (id: string, reason: string, notes?: string): Promise<void> => {
      await this.client.post(`/asset-applications/${id}/reject`, { reason, notes });
    },
  };

  // Withdrawals
  withdrawals = {
    list: async (params?: {
      page?: number;
      per_page?: number;
      status?: string;
    }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/withdrawals', { params });
      return response.data.data!;
    },

    process: async (id: string, action: 'approve' | 'reject', txHash?: string, notes?: string): Promise<void> => {
      await this.client.post(`/withdrawals/${id}/process`, {
        action,
        tx_hash: txHash,
        notes,
      });
    },
  };

  // Audit logs
  auditLogs = {
    list: async (params?: {
      page?: number;
      per_page?: number;
      admin_id?: string;
      action?: string;
      target_type?: string;
    }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/audit-logs', { params });
      return response.data.data!;
    },
  };

  // System config
  config = {
    list: async (): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>('/config');
      return response.data.data!;
    },

    update: async (key: string, value: string): Promise<void> => {
      await this.client.patch(`/config/${key}`, { value });
    },
  };

  // Roles management
  roles = {
    list: async (): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>('/roles');
      return response.data.data!;
    },

    get: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/roles/${id}`);
      return response.data.data!;
    },

    create: async (data: {
      name: string;
      display_name: string;
      description?: string;
      permissions: string[];
    }): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>('/roles', data);
      return response.data.data!;
    },

    update: async (id: string, data: {
      display_name?: string;
      description?: string;
      permissions?: string[];
    }): Promise<void> => {
      await this.client.put(`/roles/${id}`, data);
    },

    delete: async (id: string): Promise<void> => {
      await this.client.delete(`/roles/${id}`);
    },

    getPermissions: async (): Promise<{
      permissions: any[];
      grouped: Record<string, any[]>;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/permissions');
      return response.data.data!;
    },
  };

  // Admin users management
  admins = {
    list: async (): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>('/admins');
      return response.data.data!;
    },

    updateRole: async (id: string, roleId: string): Promise<void> => {
      await this.client.patch(`/admins/${id}/role`, { role_id: roleId });
    },
  };

  // Token Splits Management
  tokenSplits = {
    list: async (params?: {
      page?: number;
      per_page?: number;
      status?: string;
    }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/token-splits', { params });
      return response.data.data!;
    },

    getStats: async (): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>('/token-splits/stats');
      return response.data.data!;
    },

    get: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/token-splits/${id}`);
      return response.data.data!;
    },

    create: async (data: {
      assetId: string;
      splitType: 'split' | 'reverse_split';
      ratioFrom: number;
      ratioTo: number;
      fractionalHandling?: string;
      effectiveDate: string;
      announcementTime?: string;
      recordDate?: string;
      exDate?: string;
      tradingHaltStart?: string;
      tradingHaltEnd?: string;
    }): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>('/token-splits', data);
      return response.data.data!;
    },

    approve: async (id: string): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>(`/token-splits/${id}/approve`);
      return response.data.data!;
    },

    cancel: async (id: string, reason?: string): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>(`/token-splits/${id}/cancel`, { reason });
      return response.data.data!;
    },

    createSnapshot: async (id: string): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>(`/token-splits/${id}/snapshot`);
      return response.data.data!;
    },

    execute: async (id: string): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>(`/token-splits/${id}/execute`);
      return response.data.data!;
    },

    getDetails: async (id: string, params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>(`/token-splits/${id}/details`, { params });
      return response.data.data!;
    },

    getOrderAdjustments: async (id: string, params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>(`/token-splits/${id}/order-adjustments`, { params });
      return response.data.data!;
    },

    getPriceAdjustments: async (id: string, params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>(`/token-splits/${id}/price-adjustments`, { params });
      return response.data.data!;
    },

    announce: async (id: string, data: { title: string; content: string; channels?: string[] }): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>(`/token-splits/${id}/announce`, data);
      return response.data.data!;
    },

    getAnnouncements: async (id: string): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>(`/token-splits/${id}/announcements`);
      return response.data.data!;
    },

    getByAsset: async (assetId: string, params?: { page?: number; per_page?: number }): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>(`/token-splits/asset/${assetId}`, { params });
      return response.data.data!;
    },
  };

  // Bonding Curve LP Token Management
  bondingLP = {
    getMigrationSnapshots: async (migrationId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/bonding/${migrationId}/snapshots`);
      return response.data.data!;
    },

    getLPClaimStats: async (migrationId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/bonding/${migrationId}/lp-claim-stats`);
      return response.data.data!;
    },

    completeMigration: async (migrationId: string): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>(`/bonding/${migrationId}/complete-migration`);
      return response.data.data!;
    },
  };
}

export const adminApi = new AdminApiClient();

// Initialize token from localStorage
const savedToken = localStorage.getItem('admin_token');
if (savedToken) {
  adminApi.setToken(savedToken);
}

// Alias for convenience
export const api = {
  admin: {
    getConfigs: adminApi.config.list,
    updateConfig: adminApi.config.update,
    ...adminApi,
  },
};
